import "./styles.scss";

import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import * as XLSX from "xlsx";
import CustomModal from "@components/CustomModal";
import { Text, Button, StatusChip } from "@fragments";
import {
  IconCloudUpload,
  IconFileSpreadsheet,
  IconTrash,
  IconDownload,
  IconUpload,
} from "@tabler/icons-react";

import api from "@helpers/api";

const ACCEPT = ".xlsx,.xls,.csv";
const VALID_EXTENSIONS = ["xlsx", "xls", "csv"];
const MAX_BYTES = 5 * 1024 * 1024;

// Client-side .xlsx template (header + sample row) so the owner can fill the
// expected columns offline. Built with SheetJS, matching the import format.
// The header row is the API contract and stays untranslated; the sample row is
// illustrative, so it follows the active language.
const getTemplateRows = (t) => [
  ["email", "name", "phone"],
  ["exemplo@email.com", t("import.templateSampleName"), "+5511999999999"],
];

/**
 * Bulk import — §6.6. Imperative open: modalRef.current.open(program, onDone).
 * Drop zone (.xlsx/.xls/.csv, ≤5MB) → multipart FormData → 202 (async). The
 * file is sent as-is (NOT the presigned-S3 flow) since the endpoint ingests it.
 */
export default function ImportModal({ modalRef, communityId }) {
  const { t } = useTranslation("mentoringPrograms");
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [file, setFile] = useState(null);
  const [dragOver, setDragOver] = useState(false);

  const inputRef = useRef(null);
  const dragDepthRef = useRef(0);
  const programRef = useRef(null);
  const onDoneRef = useRef(null);

  modalRef.current = {
    open(program, onDone) {
      programRef.current = program ?? null;
      onDoneRef.current = onDone;
      setFile(null);
      setDragOver(false);
      dragDepthRef.current = 0;
      setOpen(true);
    },
  };

  const program = programRef.current;

  function selectFile(selected) {
    if (!selected) return;
    const ext = selected.name.split(".").pop()?.toLowerCase();
    if (!VALID_EXTENSIONS.includes(ext)) {
      window.setSnackbar?.(t("import.invalidFormat"));
      return;
    }
    if (selected.size > MAX_BYTES) {
      window.setSnackbar?.(t("import.fileTooLarge"));
      return;
    }
    setFile(selected);
  }

  function handleInputChange(event) {
    selectFile(event?.target?.files?.[0]);
    if (event?.target) event.target.value = null;
  }

  function handleDrop(event) {
    event.preventDefault();
    dragDepthRef.current = 0;
    setDragOver(false);
    selectFile(event.dataTransfer?.files?.[0]);
  }

  function handleDragEnter(event) {
    event.preventDefault();
    dragDepthRef.current += 1;
    setDragOver(true);
  }

  function handleDragLeave(event) {
    event.preventDefault();
    dragDepthRef.current -= 1;
    if (dragDepthRef.current <= 0) {
      dragDepthRef.current = 0;
      setDragOver(false);
    }
  }

  function downloadTemplate() {
    const sheet = XLSX.utils.aoa_to_sheet(getTemplateRows(t));
    sheet["!cols"] = [{ wch: 28 }, { wch: 24 }, { wch: 20 }];
    const book = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(book, sheet, t("import.templateSheetName"));
    XLSX.writeFile(book, t("import.templateFilename"));
  }

  async function handleSubmit() {
    if (!file) {
      window.setSnackbar?.(t("import.selectFile"));
      return;
    }

    setLoading(true);

    const formData = new FormData();
    formData.append("file", file);

    const res = await api.post(
      `community/${communityId}/mentoring/programs/${program?.id}/import`,
      formData,
    );

    setLoading(false);

    if (res?.response?.status >= 400) {
      window.setSnackbar?.(res.response.data?.message || t("import.invalidFile"));
      return;
    }

    window.setSnackbar?.(t("import.started"));
    onDoneRef.current?.();
    setOpen(false);
  }

  return (
    <CustomModal
      maxWidth={560}
      open={open}
      handleClose={() => setOpen(false)}
      title={t("import.title")}
      bottomButtonSumbitText={t("import.submit")}
      bottomButtonSumbitIcon={<IconUpload size={18} />}
      handleSubmit={handleSubmit}
      loading={loading}
      disabled={!file}
      bodyComponent={
        open ? (
          <div className="import-modal">
            <Text.span contrast={6}>
              {program
                ? t("import.descriptionWithProgram", { program: program.name })
                : t("import.description")}
            </Text.span>

            <button
              type="button"
              className="import-template"
              onClick={downloadTemplate}
            >
              <span className="import-template-icon">
                <IconFileSpreadsheet size={18} />
              </span>
              <span className="import-template-text">
                <Text.span contrast={8} weight={500}>
                  {t("import.downloadTemplate")}
                </Text.span>
                <Text.span contrast={5}>
                  {t("import.templateColumns")}
                </Text.span>
              </span>
              <IconDownload size={16} className="import-template-chevron" />
            </button>

            {file ? (
              <div className="import-file">
                <span className="import-file-icon">
                  <IconFileSpreadsheet size={20} />
                </span>
                <div className="import-file-details">
                  <Text.h6 contrast={8} weight={500} maxLines={1}>
                    {file.name}
                  </Text.h6>
                  <div className="import-file-meta">
                    <StatusChip.Default text={t("import.fileReady")} />
                    <Text.span contrast={5}>{formatBytes(file.size)}</Text.span>
                  </div>
                </div>
                <Button
                  color="overlay"
                  size="small"
                  icon={<IconTrash size={16} />}
                  tooltip={t("import.removeFile")}
                  onClick={() => setFile(null)}
                  preventLoading
                />
              </div>
            ) : (
              <div
                className={`import-dropzone${dragOver ? " is-drag-over" : ""}`}
                onClick={() => inputRef.current?.click()}
                onDrop={handleDrop}
                onDragOver={(e) => e.preventDefault()}
                onDragEnter={handleDragEnter}
                onDragLeave={handleDragLeave}
                role="button"
                tabIndex={0}
              >
                <span className="import-dropzone-icon">
                  <IconCloudUpload size={26} />
                </span>
                <Text.h6 contrast={8} weight={500}>
                  {t("import.dropzoneTitle")}
                </Text.h6>
                <Text.span contrast={5}>
                  {t("import.dropzoneHint")}
                </Text.span>
                <input
                  ref={inputRef}
                  type="file"
                  accept={ACCEPT}
                  onChange={handleInputChange}
                  className="import-dropzone-input"
                />
              </div>
            )}
          </div>
        ) : null
      }
    />
  );
}

function formatBytes(bytes) {
  if (!bytes && bytes !== 0) return "";
  const units = ["B", "KB", "MB"];
  let size = bytes;
  let i = 0;
  while (size >= 1024 && i < units.length - 1) {
    size /= 1024;
    i += 1;
  }
  return `${size.toFixed(size >= 100 || i === 0 ? 0 : 1)} ${units[i]}`;
}
